//App.js

import { HelmetProvider } from "react-helmet-async";
import React, {
  useState,
  useEffect,
  lazy,
  Suspense,
  useRef,
  useContext,
} from "react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Login from "./login";
import { FaSpinner } from "react-icons/fa";
import { auth } from "./firebase";
import { signOut } from "firebase/auth";
import { Helmet } from "react-helmet";
import CatchAll from "./CatchAll";
import { AuthContext } from "./AuthProvider";
import {
  useParams,
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  Navigate,
} from "react-router-dom";
import HomeLayout from "./HomeLayout";
import GoRedirect from "./GoRedirect"; // Import the GoRedirect component
import "./App.css";
import AnalyticsLogger from "./AnalyticsLogger";
import { aiSdrRoutes } from "./ai-sdr";
import { DirectDummyDashboard } from "./ai-sdr"; // Import the direct dashboard component
import DataNextRoutes from "./data-next";
// Lazy load components
const AUDIOBOT = lazy(() => import("./audioChatBot"));
const AUDIOBOTRE = lazy(() => import("./audioChatBotRe"));
const AUDIOBOTHG = lazy(() => import("./audioChatBotHG"));
const AUDIOBOTWPP = lazy(() => import("./audioChatBotWPP"));
const AUDIOBOTBR = lazy(() => import("./audioChatBotBR"));
const AUDIOBOTAZURE = lazy(() => import("./audioChatBotAzure"));
const MOBILE = lazy(() => import("./mobile"));
const FAQ = lazy(() => import("./FAQ"));
const Mission = lazy(() => import("./mission"));
const QRCodeGenerator = lazy(() => import("./QRCodeGenerator"));
const ThumbnailCreator = lazy(() => import("./ThumbnailCreator"));
const Admin = lazy(() => import("./AdminPage"));
const Food = lazy(() => import("./Food"));
const GoForm = lazy(() => import("./components/GoForm"));
const GoList = lazy(() => import("./components/GoList"));
const Go = lazy(() => import("./Go"));
const OKR = lazy(() => import("./OKR"));
const VideoCreate = lazy(() => import("./VideoCreate"));
const Guide = lazy(() => import("./Guide"));
const GuideMD = lazy(() => import("./GuideMD"));
const DocMD = lazy(() => import("./DocMD"));
const Examples = lazy(() => import("./example"));
const GuideMDServer = lazy(() => import("./GuideMDServer"));
const MyGuides = lazy(() => import("./MyGuide"));
const MyVideo = lazy(() => import("./MyVideo"));
const Course = lazy(() => import("./Course"));
const My = lazy(() => import("./My"));
const TutorialCreate = lazy(() => import("./TutorialCreate"));
const LectureCreate = lazy(() => import("./LectureCreate"));
const Quiz = lazy(() => import("./QuizCreate"));
const Family = lazy(() => import("./Family"));
const ShowPublicQuiz = lazy(() => import("./components/quiz/showPublicQuiz"));
const MyQuiz = lazy(() => import("./components/quiz/myQuiz"));
const DreamLayout = lazy(() => import("./DreamLayout"));
const SubscribePage = lazy(() => import("./SubscribePage"));
const PrivacyLayout = lazy(() => import("./PrivacyLayout"));
const ShowVideo = lazy(() => import("./components/ShowGuide"));
const TextToAudioConverter = lazy(() => import("./TextToAudioConverter"));
const Tags = lazy(() => import("./tags"));
const ChatGPT = lazy(() => import("./chat"));
const ChatGPTVieu = lazy(() => import("./chat-vieu"));
const ChatGPTPDX = lazy(() => import("./chat-pdx"));
const ChatGPTOodle = lazy(() => import("./chat-oodle"));
const ChatBot = lazy(() => import("./chatbot"));
const ChatAdmin = lazy(() => import("./chatadmin"));
const ChatBotIframePage = lazy(() => import("./chatbotiframe"));
const ChatBotIframeVieuPage = lazy(() => import("./chatbotiframe-vieu"));
const ChatBotIframeOodlePage = lazy(() => import("./chatbotiframe-oodle"));
const ChatBotIframePDXPage = lazy(() => import("./chatbotiframe-pdx"));
const ChatBotIframeEmbed = lazy(() => import("./ChatBotIframeEmbed"));
const ExhibitNow = lazy(() => import("./demo/skyline/ExhibitNow"));
const AIDesigner = lazy(() => import("./demo/skyline/AIDesigner"));

const BlogToVideo = lazy(() => import("./BlogToVideo"));
const ChatbotCreator = lazy(() => import("./chat/ChatbotCreator"));
const VideoAll = lazy(() => import("./chat/videoAll"));
const Tools = lazy(() => import("./tools"));

// With lazy loading for AI SDR
const AiSdr = lazy(() => import("./ai-sdr"));

// Add lazy loading for AI-360
const Ai360 = lazy(() => import("./ai-360"));

// With lazy loading for Image Editor
const ImageEditor = lazy(() => import("./image-editor"));
const VideoEditor = lazy(() => import("./video-editor"));

// With lazy loading for Business-360
const Business360 = lazy(() => import("./business-360"));

// With lazy loading for AI SDR Ventura
const AiSdrVentura = lazy(() => import("./ai-sdr-ventura"));

// With lazy loading for AI Agent
const AiAgent = lazy(() => import("./ai-agent"));

// With lazy loading for Echo
const Echo = lazy(() => import("./echo"));

// With lazy loading for Vision
const Vision = lazy(() => import("./vision"));

// With lazy loading for Guided Buying
const GuidedBuying = lazy(() => import("./guided-buying"));

// With lazy loading for AI Recruiter
const AiRecruiter = lazy(() => import("./ai-recruiter"));

// With lazy loading for AI Recruiter CRM
const AiRecruiterCRM = lazy(() => import("./ai-recruiter-crm"));

// With lazy loading for AI HCM
const AiHcm = lazy(() => import("./ai-hcm"));

// With lazy loading for AI SDR Athena
const AiSdrAthena = lazy(() => import("./ai-sdr-athena"));

// With lazy loading for Extension Teams
const ExtensionTeam = lazy(() => import("./extension-team"));

// With lazy loading for BRD Assist
const BrdAssist = lazy(() => import("./brdAssist"));

// With lazy loading for Grant AI
const GrantAI = lazy(() => import("./grant-ai"));
// With lazy loading for Grant AI Chat
const GrantAIChat = lazy(() => import("./grant-ai-chat"));

// With lazy loading for File Upload App
const FileUpload = lazy(() => import("./file-upload"));

// With lazy loading for GoAIGold - Jewelry Virtual Try-On
const Gold = lazy(() => import("./gold"));

// With lazy loading for Talk-to-Data - KPI Dashboard with Chat
const TalkToData = lazy(() => import("./talk-to-data"));

// With lazy loading for AI Meeting - Full Meeting Assistant Platform
const AiMeeting = lazy(() => import("./ai-meeting"));
const PowerBIApp = lazy(() => import("./power-bi"));

// With lazy loading for AI Connect - Real-time Voice Communications
const AiConnect = lazy(() => import("./ai-connect"));

// AI Connect V1 - Verification Agent (BRD-based)
const AiConnectV1 = lazy(() => import("./ai-connect-v1"));

// AI Employment Verification App
const AiEmploymentVerificationApp = lazy(() =>
  import("./ai-employment-verification-app")
);

// AI Terumo - SQP Training Transformation Platform
const AiTerumo = lazy(() => import("./ai-terumo"));

// AI Process Intelligence - Agentic Process Discovery Platform
const AiProcessIntel = lazy(() => import("./ai-process-intel"));

// Video Transcript Knowledge Base - AI-powered video transcription & search
const VideoTranscriptKB = lazy(() => import("./video-transcript-kb"));

// AuxoAI Demo Hub - Internal docs & demo portal
const DemosApp = lazy(() => import("./demos"));

// LiveLoop - Agentic Inbound Revenue Orchestration
const LiveLoop = lazy(() => import("./LiveLoop"));

function AppContent() {
  const [showLogin, setShowLogin] = useState(false);
  const [loginNotification, setLoginNotification] = useState(false);
  const [notificationShown, setNotificationShown] = useState(false);
  const [logoutNotification, setLogoutNotification] = useState(false);

  const renderCount = useRef(0);
  const authContextValue = useContext(AuthContext);
  const { user } = useContext(AuthContext);
  const location = useLocation(); // get the current location
  const [shouldHideFooter, setShouldHideFooter] = useState(false);

  const triggerPaths = [
    "/create-chatbot",
    "/ai",
    "/guide",
    "/doc",
    "/blog-to-video",
    "/myvideo",
    "/tc",
    "/logo",
    "/audio",
    "/qr",
    "/skyline",
    "/ai-designer",
  ];

  const autoShowLogin = triggerPaths.some((path) =>
    location.pathname.startsWith(path)
  );

  // Define paths to hide the footer
  const hideFooterPaths = [
    "/ai",
    "/guide",
    "/doc",
    "/video",
    "/myvideo",
    "/tc",
    "/logo",
    "/banner",
    "/aiserver",
    "/qr",
    "/college",
    "/myguide",
    "/audio",
    "/example",
    "/help",
    "/training",
    "/chatbot",
    "/chatadmin",
    "/chat",
    "/view",
    "/agent",
    "/chat-vieu",
    "/chat-oodle",
    "/chat-pdx",
    "/tag",
    "/iframe-embed",
    "/iframe-embed-oodle",
    "/iframe-embed-pdx",
    "/blog-to-video",
    "/videoall",
    "/chat-id",
    "/create-chatbot",
    "/mobile",
    "/skyline",
    "/ai-designer",
    "/ai-agent",
    "/echo",
    "/ai-recruiter",
    "/ai-recruiter-crm",
    "/ai-sdr-athena",
    "/ai-360",
    "/image-editor",
    "/business-360",
    "/file-upload",
    "/talk-to-data",
    "/ai-meeting",
    "/ai-connect",
    "/ai-connect-v1",
    "/ai-employment-verification-app",
    "/ai-terumo",
    "/ai-process-intel",
    "/video-transcript-kb",
    "/demos",
  ];

  // Define isVideoEmbed and isPup outside of useEffect for global use
  const query = new URLSearchParams(window.location.search);
  const isEmbed = query.get("embed") === "true";
  const isIframeMode = query.get("iframe") === "true";

  const isVideoEmbed =
    (location.pathname.includes("/video") ||
      location.pathname.includes("/view")) &&
    query.get("embed") === "true";
  let isIFrameEmbed;
  isIFrameEmbed =
    location.pathname.includes("/iframe-embed") ||
    location.pathname.includes("/mobile") ||
    location.pathname.includes("/iframe-embed-vieu") ||
    location.pathname.includes("/iframe-embed-oodle");

  const isPup = query.get("pup") === "true";

  // Check if the path or query parameter should hide the footer
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);

    const shouldHide =
      isPup ||
      hideFooterPaths.some((path) => location.pathname.startsWith(path));

    setShouldHideFooter(shouldHide);
    // console.log("shouldHideFooter", shouldHide);
  }, [location]); // Run this effect whenever location changes

  // Show login notification
  useEffect(() => {
    if (user && !notificationShown) {
      setLoginNotification(true);
      setNotificationShown(true);
      setTimeout(() => {
        setLoginNotification(false);
      }, 3000); // Hide after 3 seconds
    }
  }, [user, notificationShown]);

  // Handle logout and show notification
  const handleSignOut = () => {
    signOut(auth)
      .then(() => {
        setLogoutNotification(true);
        setTimeout(() => {
          setLogoutNotification(false);
          window.location.reload(); // Refresh the page after signout
        }, 3000); // Notification duration before reload
      })
      .catch((error) => {
        console.error("Error signing out:", error);
      });
  };

  const handleLoginClick = () => {
    setShowLogin(true);
  };

  const closeLogin = () => {
    setShowLogin(false);
  };

  function generateTitleAndDescriptionFromPath(path) {
    const pathSegments = path.split("/").filter((segment) => segment !== "");

    // Adjusted function to exclude only unwanted patterns of short length
    const isValidSegment = (segment) => {
      // Exclude segments that are only letters followed by numbers, or numbers followed by letters, and are shorter than or equal to 10 characters
      const regex = /^[-_]?[a-zA-Z]+?\d+[-_]?$|^[-_]?\d+?[a-zA-Z]+[-_]?$/i;
      return !(regex.test(segment) && segment.length <= 10);
    };

    // Clean up segments by removing unwanted patterns at the end
    const cleanSegment = (segment) => {
      // Remove trailing patterns like '-abc123' or '-123abc'
      return segment.replace(/[-_][a-zA-Z]*\d{3,}$|[-_]\d{3,}[a-zA-Z]*$/i, "");
    };

    const filteredSegments = pathSegments.filter(isValidSegment);
    const cleanedSegments = filteredSegments.map(cleanSegment);

    const capitalizedSegments = cleanedSegments.map((segment) =>
      segment
        .split("-")
        .map((word) => capitalize(word))
        .join(" ")
    );

    if (capitalizedSegments.length > 0) {
      const newTitle = `AuxoAI: ${capitalizedSegments.join(", ")}`;
      const newDescription = `Explore Chat, Videos and Guides with AuxoAI on ${capitalizedSegments.join(
        ", "
      )}`;
      return { title: newTitle, description: newDescription };
    } else {
      // Return default title and description
      return {
        title: "AuxoAI - An AI Agent - GuideMe, ShowMe, DoItForMe, TalkToMe",
        description:
          "AuxoAI's AI Agent can not only answer questions, show custom videos, but it can also give demos and complete actions on behalf of the user!",
      };
    }
  }

  function capitalize(text) {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  function DynamicMetaTags() {
    const location = useLocation();
    const [title, setTitle] = useState(
      "AuxoAI - An AI Agent - GuideMe, ShowMe, DoItForMe, TalkToMe"
    );
    const [description, setDescription] = useState(
      "AuxoAI's AI Agent can not only answer questions, show custom videos, but it can also give demos and complete actions on behalf of the user!"
    );

    useEffect(() => {
      const path = location.pathname;
      let newTitle;
      let newDescription;

      // Handle dynamic routes
      if (path.match(/^\/quiz\/\d+/)) {
        newTitle = "Quiz - AuxoAI";
        newDescription = "Take a quiz on AuxoAI.";
      } else if (path.match(/^\/family\/[^/]+/)) {
        const familyName = path.split("/")[2];
        newTitle = `Family Tree of ${capitalize(familyName)} - AuxoAI`;
        newDescription = `Discover the Family Tree of ${capitalize(
          familyName
        )}. Explore your heritage and uncover the stories of your ancestors.`;
      } else if (path.match(/^\/go\/[^/]+/)) {
        newTitle = "Redirecting... - AuxoAI";
        newDescription = "Redirecting to the destination.";
      } else if (path.match(/^\/tag\/[^/]+/)) {
        const tagName = path.split("/")[2];
        newTitle = `Tag: ${capitalize(tagName)} - AuxoAI`;
        newDescription = `Explore content tagged with ${capitalize(tagName)}.`;
      } else if (path.match(/^\/\d{4,}$/)) {
        newTitle = "Quiz - AuxoAI";
        newDescription = "Take a quiz on AuxoAI.";
      } else {
        // Static routes
        switch (path) {
          case "/":
            newTitle =
              "AuxoAI - An AI Agent - GuideMe, ShowMe, DoItForMe, TalkToMe";
            newDescription =
              "Chat, Videos and Guided Automation. AuxoAI's AI Agent can not only answer questions, show custom videos, but it can also give demos and complete actions on behalf of the user!";
            break;
          case "/privacy-security-cookie":
            newTitle = "Privacy, Security, and Cookies - AuxoAI";
            newDescription =
              "Learn about our policies on privacy, security, and cookies.";
            break;
          case "/guide":
            newTitle = "Create How-To Guides - AuxoAI";
            newDescription =
              "Develop step-by-step How-To guides to Drive Sales and Customer Success.";
            break;
          case "/guide-md":
            newTitle = "Markdown Guide Creation - AuxoAI";
            newDescription = "Use Markdown to create impactful guides.";
            break;
          case "/create":
            newTitle = "Start Creating - AuxoAI";
            newDescription =
              "Begin creating guides, videos, and chatbots to promote AI features.";
            break;
          case "/blog-to-video":
            newTitle = "Blog To Video - AuxoAI";
            newDescription = "Easily convert blogs into Videos - AuxoAI.";
            break;
          case "/myguide":
            newTitle = "My Guides - AuxoAI";
            newDescription = "View and manage your guides.";
            break;
          case "/myvideo":
            newTitle = "My Videos - AuxoAI";
            newDescription = "View and manage your videos.";
            break;
          case "/video":
          case "/view":
            newTitle = "Create Videos - AuxoAI";
            newDescription = "Produce video.";
            break;
          case "/course":
            newTitle = "Develop Courses - AuxoAI";
            newDescription =
              "Create courses to educate and Drive Sales and Customer Success.";
            break;
          case "/quiz":
            newTitle = "Create Quizzes - AuxoAI";
            newDescription = "Design quizzes to enhance AI learning.";
            break;
          case "/login":
            newTitle = "Login - AuxoAI";
            newDescription =
              "Access your account to create Sales and Customer Success content.";
            break;
          case "/tutorial":
            newTitle = "Create Tutorials - AuxoAI";
            newDescription =
              "Build tutorials to facilitate Sales and Customer Success.";
            break;
          case "/lecture":
            newTitle = "Create Lectures - AuxoAI";
            newDescription = "Craft lectures to promote AI understanding.";
            break;
          case "/subscribe":
            newTitle = "Subscribe - AuxoAI";
            newDescription =
              "Subscribe to access AI tools for creating adoption content.";
            break;
          case "/my":
            newTitle = "My Account - AuxoAI";
            newDescription = "Manage your profile and creations.";
            break;
          case "/myquiz":
            newTitle = "My Quizzes - AuxoAI";
            newDescription = "View your quiz history.";
            break;
          case "/family":
            newTitle = "Family Tree - AuxoAI";
            newDescription = "Explore family trees.";
            break;
          case "/privacy":
            newTitle = "Privacy Policy - AuxoAI";
            newDescription = "Read our privacy policy.";
            break;
          case "/security":
            newTitle = "Security Policy - AuxoAI";
            newDescription = "Read our security policy.";
            break;
          case "/cookie":
            newTitle = "Cookie Policy - AuxoAI";
            newDescription = "Read our cookie policy.";
            break;
          case "/qr":
            newTitle = "QR Code Generator - AuxoAI";
            newDescription = "Generate QR codes effortlessly.";
            break;
          case "/tc":
            newTitle = "Thumbnail Creator - AuxoAI";
            newDescription = "Create thumbnails easily.";
            break;
          case "/logo":
            newTitle = "Logo Creator - AuxoAI";
            newDescription = "Design logos effortlessly.";
            break;
          case "/food":
            newTitle = "Food - AuxoAI";
            newDescription = "Explore food-related content.";
            break;
          case "/admin":
            newTitle = "Admin - AuxoAI";
            newDescription = "Administrative panel.";
            break;
          case "/create-video-with-ai":
            newTitle = "Create Video with AI - AuxoAI";
            newDescription = "Create videos using AI technology.";
            break;
          case "/ai":
            newTitle = "Create Video with AI - AuxoAI";
            newDescription =
              "Discover our AI capabilities for Video Tutorials - Create Now!";
            break;
          case "/banner":
            newTitle = "Banner Creator - AuxoAI";
            newDescription = "Design banners easily.";
            break;
          case "/audio":
            newTitle = "Text to Audio Converter - AuxoAI";
            newDescription = "Convert text to audio seamlessly.";
            break;
          case "/example":
            newTitle = "Examples - AuxoAI";
            newDescription = "Browse through examples.";
            break;
          case "/training":
            newTitle = "Training - AuxoAI";
            newDescription = "Access training resources.";
            break;
          case "/tags":
            newTitle = "Tags - AuxoAI";
            newDescription = "Explore content by tags.";
            break;
          case "/chat-vieu":
            newTitle = "Try Vieu - Chatbot";
            newDescription =
              " AI-powered tools to enhance B2B sales through warm introductions";
            break;
          case "/chat-oodle":
            newTitle = "Try Oodle - Chatbot";
            newDescription =
              "Fully managed serverless metrics observability platform ";
            break;
          case "/chat":
            newTitle = "Create Agent - AuxoAI";
            newDescription = "Build Agents trained on your products.";
            break;
          case "/chatbot":
            newTitle = "Interact with Agent - AuxoAI";
            newDescription =
              "Engage with chatbots to learn and Drive Sales and Customer Success.";
            break;
          case "/chatbot-id":
            newTitle = "Interact with The ChatBot";
            newDescription =
              "Engage with chatbots to learn and Drive Sales and Customer Success.";
            break;
          case "/chatadmin":
            newTitle = "Chatbots - Admin";
            newDescription =
              "Engage with chatbots to learn and Drive Sales and Customer Success.";
            break;
          default:
            // If no match, leave newTitle and newDescription undefined
            break;
        }
      }

      if (!newTitle || !newDescription) {
        // If newTitle or newDescription not set, generate them based on URL path
        const { title: generatedTitle, description: generatedDescription } =
          generateTitleAndDescriptionFromPath(path);
        newTitle = generatedTitle;
        newDescription = generatedDescription;
      }

      setTitle(newTitle);
      setDescription(newDescription);
    }, [location]);

    return (
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />

        {/* Open Graph Meta Tags */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content="https://www.kimavi.com/hc.jpg" />
        <meta
          property="og:url"
          content={`https://www.kimavi.com${location.pathname}`}
        />
        <meta property="og:type" content="website" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@AuxoAI3" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta
          name="twitter:image"
          content="https://www.kimavi.com/images/logo.png"
        />
      </Helmet>
    );
  }

  function capitalize(text) {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  if (!authContextValue) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <FaSpinner icon="spinner" className="animate-spin" size={50} />
      </div>
    );
  }

  function QuizRoute() {
    let { quizId } = useParams();
    if (quizId && quizId.match(/^\d{4,}$/)) {
      return <ShowPublicQuiz />;
    } else {
      return <CatchAll />;
    }
  }

  // Helper component to redirect to ai-sdr/dashboard while preserving query parameters
  function DashboardRedirect() {
    const location = useLocation();
    return <Navigate replace to={`/ai-sdr/dashboard${location.search}`} />;
  }

  return (
    <>
      <DynamicMetaTags />
      <AnalyticsLogger />
      {!isEmbed &&
        !isVideoEmbed &&
        !isPup &&
        !isIFrameEmbed &&
        !isIframeMode &&
        !location.pathname.startsWith("/skyline") &&
        !location.pathname.startsWith("/ai-designer") &&
        !location.pathname.startsWith("/agent") &&
        !location.pathname.startsWith("/ai-agent") &&
        !location.pathname.startsWith("/ai-meeting") &&
        !location.pathname.startsWith("/ai-connect") &&
        !location.pathname.startsWith("/ai-connect-v1") &&
        !location.pathname.startsWith("/ai-employment-verification-app") &&
        !location.pathname.startsWith("/ai-terumo") &&
        !location.pathname.startsWith("/ai-process-intel") &&
        !location.pathname.startsWith("/video-transcript-kb") &&
        !location.pathname.startsWith("/demos") && (
          <Navbar
            onLoginClick={handleLoginClick}
            handleSignOut={handleSignOut}
            autoShowLogin={autoShowLogin}
          />
        )}
      {showLogin && <Login closeLogin={() => setShowLogin(false)} />}
      {logoutNotification && (
        <div className="fixed top-0 right-0 bg-green-500 text-white px-4 py-2 rounded">
          Logout successful
        </div>
      )}
      <div
        className={`flex-grow w-full h-full ${!isEmbed &&
          !isVideoEmbed &&
          !isPup &&
          !isIFrameEmbed &&
          !isIframeMode &&
          !location.pathname.startsWith("/skyline") &&
          !location.pathname.startsWith("/ai-designer") &&
          !location.pathname.startsWith("/ai-agent") &&
          !location.pathname.startsWith("/ai-meeting") &&
          !location.pathname.startsWith("/ai-connect") &&
          !location.pathname.startsWith("/ai-connect-v1") &&
          !location.pathname.startsWith("/ai-employment-verification-app") &&
          !location.pathname.startsWith("/ai-terumo") &&
          !location.pathname.startsWith("/ai-process-intel") &&
          !location.pathname.startsWith("/video-transcript-kb") &&
          !location.pathname.startsWith("/demos")
            ? "pt-12 md:pt-20"
            : ""
        }`}
      >
        <div className="w-full h-full flex-grow">
          <Suspense fallback={<div>Loading...</div>}>
            <Routes>
              {/* Define your routes */}
              <Route
                path="/privacy-security-cookie"
                element={<PrivacyLayout />}
              />
              <Route exact path="/" element={<HomeLayout />} />
              <Route exact path="/skyline" element={<ExhibitNow />} />
              <Route exact path="/ai-designer" element={<AIDesigner />} />
              <Route exact path="/TalkToMe" element={<AUDIOBOT />} />
              <Route exact path="/reworld-talk" element={<AUDIOBOTRE />} />
              <Route exact path="/hgi-talk" element={<AUDIOBOTHG />} />
              <Route exact path="/wwt-talk" element={<AUDIOBOTWPP />} />
              <Route exact path="/fogodechao-talk" element={<AUDIOBOTBR />} />
              <Route exact path="/azure-talk" element={<AUDIOBOTAZURE />} />
              <Route exact path="/mobile" element={<MOBILE />} />
              <Route exact path="/faq" element={<FAQ />} />
              <Route path="/agent/:botId" element={<ChatBotIframeEmbed />} />
              <Route path="/chat-id/:botId" element={<ChatBotIframeEmbed />} />
              <Route path="/:quizId" element={<QuizRoute />} />
              <Route path="/guide" element={<Guide />} />
              <Route path="/guide-md" element={<GuideMD />} />
              <Route path="/doc" element={<DocMD />} />
              <Route path="/create" element={<Guide />} />
              <Route path="/videoAll" element={<VideoAll />} />
              <Route path="/myguide" element={<MyGuides />} />
              <Route path="/myvideo" element={<VideoAll />} />
              <Route path="/video" element={<ShowVideo />} />
              <Route path="/view" element={<ShowVideo />} />
              <Route path="/course" element={<Course />} />
              <Route path="/quiz" element={<Quiz />} />
              <Route path="/login" element={<Login />} />
              <Route path="/quiz/:quizId" element={<ShowPublicQuiz />} />
              <Route path="/tutorial" element={<TutorialCreate />} />
              <Route path="/lecture" element={<LectureCreate />} />
              <Route path="/go/:name" element={<GoRedirect />} />
              <Route path="/subscribe" element={<SubscribePage />} />
              <Route path="/my" element={<My />} />
              <Route path="/myquiz" element={<MyQuiz />} />
              <Route path="/family/:familyName" element={<Family />} />
              <Route path="/family" element={<Family />} />
              <Route path="/privacy" element={<PrivacyLayout />} />
              <Route path="/security" element={<PrivacyLayout />} />
              <Route path="/cookie" element={<PrivacyLayout />} />
              <Route path="/qr" element={<QRCodeGenerator />} />
              <Route path="/tc" element={<ThumbnailCreator />} />
              <Route path="/logo" element={<ThumbnailCreator />} />
              <Route path="/food" element={<Food />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/create-video-with-ai" element={<GuideMD />} />
              <Route path="/ai" element={<GuideMD />} />
              <Route path="/banner" element={<ThumbnailCreator />} />
              <Route path="/aiserver" element={<GuideMDServer />} />
              <Route path="/audio" element={<TextToAudioConverter />} />
              <Route path="/example" element={<Examples />} />
              <Route path="/training" element={<Examples />} />
              <Route path="/tags" element={<Tags />} />
              <Route path="/tag/:tagName" element={<Tags />} />
              <Route path="/chat" element={<ChatGPT />} />
              <Route path="/chat-vieu" element={<ChatGPTVieu />} />
              <Route path="/chat-oodle" element={<ChatGPTOodle />} />
              <Route path="/chat-pdx" element={<ChatGPTPDX />} />
              <Route path="/chatbot" element={<ChatBot />} />
              <Route
                path="/create-chatbot/:botId"
                element={<ChatbotCreator />}
              />
              <Route path="/create-chatbot/" element={<ChatbotCreator />} />
              <Route path="/chat-admin/*" element={<ChatAdmin />} />
              <Route path="/iframe-embed" element={<ChatBotIframePage />} />
              <Route
                path="/iframe-embed-vieu"
                element={<ChatBotIframeVieuPage />}
              />
              <Route
                path="/iframe-embed-pdx"
                element={<ChatBotIframePDXPage />}
              />
              <Route
                path="/iframe-embed-oodle"
                element={<ChatBotIframeOodlePage />}
              />
              <Route path="/blog-to-video/*" element={<BlogToVideo />} />
              <Route path="/mission/*" element={<Mission />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/ai-sdr/*" element={<AiSdr />} />
              {/* New AI-360 routes */}
              <Route path="/ai-360/*" element={<Ai360 />} />
              {/* Image Editor routes */}
              <Route path="/image-editor/*" element={<ImageEditor />} />
              {/* Video Editor routes */}
              <Route path="/video-editor/*" element={<VideoEditor />} />
              {/* Business-360 Route */}
              <Route path="/business-360/*" element={<Business360 />} />
              {/* Data-Next Route - AI Diagram Generator */}
              <Route path="/data-next/*" element={<DataNextRoutes />} />
              <Route path="/ai-sdr-ventura/*" element={<AiSdrVentura />} />
              {/* Direct route for dashboard outside of nested routes */}
              <Route path="/dashboard" element={<DashboardRedirect />} />
              <Route path="/ai-agent/*" element={<AiAgent />} />
              <Route path="/echo/*" element={<Echo />} />
              <Route path="/vision/*" element={<Vision />} />
              <Route path="/guided-buying/*" element={<GuidedBuying />} />
              <Route path="/ai-recruiter/*" element={<AiRecruiter />} />
              <Route path="/ai-recruiter-crm/*" element={<AiRecruiterCRM />} />
              <Route path="/ai-hcm/*" element={<AiHcm />} />
              <Route path="/grant-ai/*" element={<GrantAI />} />
              <Route path="/grant-ai-chat/*" element={<GrantAIChat />} />
              <Route path="/ai-sdr-athena/*" element={<AiSdrAthena />} />
              <Route path="/extension-team/*" element={<ExtensionTeam />} />
              <Route path="/brd-assist/*" element={<BrdAssist />} />
              <Route path="/file-upload/*" element={<FileUpload />} />
              {/* GoAIGold - Jewelry Virtual Try-On */}
              <Route path="/gold/*" element={<Gold />} />
              {/* Talk-to-Data - KPI Dashboard with per-card chat */}
              <Route path="/talk-to-data/*" element={<TalkToData />} />
              {/* AI Meeting - Full Meeting Assistant with Summary, Chat & Voice */}
              <Route path="/ai-meeting/*" element={<AiMeeting />} />
              {/* PowerBI Dashboard with Chat with Data */}
              <Route path="/power-bi/*" element={<PowerBIApp />} />
              {/* AI Connect - Real-time Voice Communications Platform */}
              <Route path="/ai-connect/*" element={<AiConnect />} />
              {/* AI Connect V1 - Verification Agent (BRD-based) */}
              <Route path="/ai-connect-v1/*" element={<AiConnectV1 />} />
              {/* AI Employment Verification App */}
              <Route
                path="/ai-employment-verification-app/*"
                element={<AiEmploymentVerificationApp />}
              />
              {/* AI Terumo - SQP Training Transformation Platform */}
              <Route path="/ai-terumo/*" element={<AiTerumo />} />
              {/* AI Process Intelligence - Agentic Process Discovery Platform */}
              <Route path="/ai-process-intel/*" element={<AiProcessIntel />} />
              {/* Video Transcript Knowledge Base */}
              <Route path="/video-transcript-kb/*" element={<VideoTranscriptKB />} />
              {/* LiveLoop - Agentic Inbound Revenue Orchestration */}
              <Route path="/LiveLoop/*" element={<LiveLoop />} />
              {/* AuxoAI Demo Hub */}
              <Route path="/demos/*" element={<DemosApp />} />
              <Route path="/data-next/*" element={<DataNextRoutes />} />
              <Route path="*" element={<CatchAll />} />
            </Routes>
          </Suspense>
        </div>
      </div>

      <div className="left-0 right-0 z-10  m-2 p-2">
        {!isVideoEmbed &&
          shouldHideFooter &&
          !isPup &&
          !isIFrameEmbed &&
          !isIframeMode &&
          !location.pathname.startsWith("/skyline") &&
          !location.pathname.startsWith("/ai-designer") &&
          !location.pathname.startsWith("/agent") &&
          !location.pathname.startsWith("/ai-agent") &&
          !location.pathname.startsWith("/vision") &&
          !location.pathname.startsWith("/echo") &&
          !location.pathname.startsWith("/ai-meeting") &&
          !location.pathname.startsWith("/ai-connect") &&
          !location.pathname.startsWith("/ai-connect-v1") &&
          !location.pathname.startsWith("/ai-employment-verification-app") &&
          !location.pathname.startsWith("/ai-terumo") &&
          !location.pathname.startsWith("/ai-process-intel") &&
          !location.pathname.startsWith("/video-transcript-kb") &&
          !location.pathname.startsWith("/demos") && (
            <p className="text-center text-sm text-gray-500 my-6">
              Please email{" "}
              <a
                href="mailto:info@auxoai.com"
                className="text-blue-500 underline"
              >
                info@auxoai.com
              </a>{" "}
              for help and support
            </p>
          )}
      </div>
      {/* {(isVideoEmbed || isEmbed) &&
        shouldHideFooter &&
        !location.pathname.startsWith("/skyline") && (
          <div className="text-left p-2 text-xs">
            Powered by{" "}
            <a
              href="https://www.kimavi.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              AuxoAI
            </a>
          </div>
        )} */}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AppContent />
      </Router>
    </HelmetProvider>
  );
}

export default App;
